import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

/**
 * Charts, in CSS rather than in a charting library.
 *
 * Two decisions worth knowing about.
 *
 * **No library.** Twelve numbers do not need 90KB of JavaScript on a phone on
 * mobile data at a counter in Bhaktapur. Bars whose length is a percentage of
 * the largest value are a chart a person can read without a legend, and they
 * reflow at any width for free.
 *
 * **One colour.** Every chart here is a single series showing magnitude —
 * how much money arrived, in which month, by which method — so there is no
 * identity to tell apart and nothing for a second hue to mean. That matches
 * the house rule in tokens.css: one saturated colour, spent on the primary
 * action and the "you are here" marker, and nowhere else. A dashboard in five
 * colours would be the brochure this product is deliberately not.
 *
 * Teal on white is 6.1:1, well past the 3:1 a chart mark needs.
 */

interface Column {
  readonly key: string;
  /** Under the column. Kept short — these are read at a glance. */
  readonly label: string;
  readonly value: number;
  /** The second line of the tooltip: "4 bookings". */
  readonly caption?: string;
}

interface ColumnChartProps {
  readonly columns: readonly Column[];
  readonly format: (value: number) => ReactNode;
  /** Names the whole chart for a screen reader. */
  readonly description: string;
}

/**
 * Magnitude over time. Columns, because time reads left to right.
 *
 * The tallest column carries its value above it and the rest do not: a number
 * on every bar is the thing that turns a chart back into a table. Everything
 * else is one hover away.
 */
export function ColumnChart({ columns, format, description }: ColumnChartProps): ReactElement {
  const [hovering, setHovering] = useState<string | null>(null);
  const tallest = columns.reduce((most, column) => Math.max(most, column.value), 0);
  const peak = columns.find((column) => column.value === tallest && tallest > 0);

  return (
    <div className="px-3 pb-2 pt-8 sm:px-4">
      <div className="relative flex h-44 items-end gap-0.5" role="img" aria-label={description}>
        {/* The only gridline, at the top of the tallest column. A grid of
            five recessive lines is four more than anybody counts. */}
        {tallest > 0 && (
          <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-rule">
            <span className="absolute -top-5 right-0 text-micro tabular-nums text-ink-muted">
              {format(tallest)}
            </span>
          </div>
        )}

        {columns.map((column) => {
          const height = tallest === 0 ? 0 : (column.value / tallest) * 100;
          const lit = hovering === column.key;
          return (
            <div
              key={column.key}
              className="group relative flex h-full flex-1 flex-col justify-end"
              onMouseEnter={() => setHovering(column.key)}
              onMouseLeave={() => setHovering(null)}
              onFocus={() => setHovering(column.key)}
              onBlur={() => setHovering(null)}
              tabIndex={0}
            >
              {/* The hover layer. An HTML chart is interactive whether or not
                  anybody planned for it, so it may as well answer the
                  question a hover is asking. */}
              {lit && (
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-sm border border-rule-strong bg-panel px-2 py-1 text-micro shadow-sm">
                  <span className="font-semibold">{format(column.value)}</span>
                  {column.caption !== undefined && (
                    <span className="ml-1.5 text-ink-muted">{column.caption}</span>
                  )}
                </div>
              )}

              {/* A floor of 2px on any non-zero month, so a quiet one reads as
                  "a little" rather than as "nothing recorded". */}
              <div
                className={[
                  'w-full rounded-t-[4px]',
                  column.value === 0 ? 'bg-rule' : lit ? 'bg-ink' : 'bg-signal',
                ].join(' ')}
                style={{
                  height: column.value === 0 ? '2px' : `max(2px, ${String(height)}%)`,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Labels below the axis rule, in the same 1-per-column grid as the bars. */}
      <div className="mt-1.5 flex gap-0.5 border-t border-rule pt-1.5">
        {columns.map((column) => (
          <span
            key={column.key}
            className={[
              // `min-w-0` with `truncate`: a flex child defaults to
              // min-width:auto, so a label longer than its share widens the
              // row rather than being cut. Thirteen of those is a horizontal
              // scrollbar on the page.
              'min-w-0 flex-1 truncate text-center text-micro',
              column.key === peak?.key ? 'font-semibold text-ink' : 'text-ink-muted',
            ].join(' ')}
          >
            {column.label}
          </span>
        ))}
      </div>
    </div>
  );
}

interface Share {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Under the label: a plate's make and model, or how many trips it was. */
  readonly caption?: string;
  /** Monospaced label, for a plate or a reference. */
  readonly identifier?: boolean;
  /** Where the share of the whole is worth saying, not just the length. */
  readonly showShare?: boolean;
}

interface ShareBarsProps {
  readonly rows: readonly Share[];
  readonly format: (value: number) => ReactNode;
}

/**
 * Ranked magnitude: biggest first, one bar each.
 *
 * Horizontal, because the labels are words and words read along a line. Not a
 * pie: comparing angles is harder than comparing lengths, and four slices is
 * exactly where a pie starts lying about the small ones.
 *
 * Bars are scaled against the largest row rather than against the total, so
 * the shape of the ranking is legible even when one method takes nine tenths
 * of the money. The percentage, where it is shown, carries the share — that
 * is the number people actually quote at each other.
 */
export function ShareBars({ rows, format }: ShareBarsProps): ReactElement {
  const largest = rows.reduce((most, row) => Math.max(most, row.value), 0);
  const total = rows.reduce((sum, row) => sum + row.value, 0);

  return (
    <ul className="flex flex-col divide-y divide-rule">
      {rows.map((row) => (
        <li key={row.key} className="px-3 py-2.5 sm:px-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0">
              <span className={row.identifier === true ? 'identifier' : ''}>{row.label}</span>
              {row.caption !== undefined && (
                <span className="ml-2 text-micro text-ink-muted">{row.caption}</span>
              )}
            </span>
            <span className="shrink-0 text-right">
              <span className="tabular-nums font-medium">{format(row.value)}</span>
              {row.showShare === true && total > 0 && (
                <span className="ml-2 tabular-nums text-micro text-ink-muted">
                  {Math.round((row.value / total) * 100)}%
                </span>
              )}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-sm bg-paper">
            <div
              className="h-full rounded-sm bg-signal"
              style={{ width: `${String(largest === 0 ? 0 : (row.value / largest) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
